/**
 * Map tile configuration.
 *
 * In offline deployments Nginx serves pre-downloaded tiles at /tiles/{z}/{x}/{y}.png.
 * When the tiles directory is not present Nginx proxies to OpenStreetMap automatically.
 * Online styles connect directly to third-party tile servers.
 */

export const LOCAL_TILE_URL = '/tiles/{z}/{x}/{y}.png';
export const ONLINE_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/** Whether the build is configured for offline-only mode */
export const IS_OFFLINE_BUILD = process.env.REACT_APP_OFFLINE_TILES === 'true';

/** All available map styles — offline is always included */
export const MAP_STYLES: Record<string, { name: string; url: string; attribution: string; offline?: boolean }> = {
  'local': {
    name: 'Offline / Local Tiles',
    url: LOCAL_TILE_URL,
    attribution: TILE_ATTRIBUTION,
    offline: true
  },
  'osm-hot': {
    name: 'OpenStreetMap HOT',
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles style by <a href="https://www.hotosm.org/">Humanitarian OpenStreetMap Team</a>'
  },
  'opentopomap': {
    name: 'OpenTopoMap',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="http://viewfinderpanoramas.org">SRTM</a> &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
  },
  'esri-natgeo': {
    name: 'Esri NatGeo World Map',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a> &copy; National Geographic'
  },
  'esri-gray': {
    name: 'Esri World Gray Canvas',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>'
  },
  'cartodb-positron': {
    name: 'CartoDB Positron',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  'osm-standard': {
    name: 'OpenStreetMap Standard',
    url: ONLINE_TILE_URL,
    attribution: TILE_ATTRIBUTION
  }
};

/**
 * Returns the default tile URL to use.  Set REACT_APP_OFFLINE_TILES=true in .env to
 * force local tiles, or leave unset to use online tiles.
 */
export function getTileUrl(): string {
  if (process.env.REACT_APP_OFFLINE_TILES === 'true') {
    return LOCAL_TILE_URL;
  }
  return ONLINE_TILE_URL;
}

/** Returns the default map style key based on environment */
export function getDefaultMapStyle(): string {
  return process.env.REACT_APP_OFFLINE_TILES === 'true' ? 'local' : 'esri-natgeo';
}

/** Returns the user's preferred map style from localStorage, falling back to default */
export function getPreferredMapStyle(): string {
  try {
    const saved = localStorage.getItem('mapPreferences');
    if (saved) {
      const prefs = JSON.parse(saved);
      if (prefs.mapStyle && MAP_STYLES[prefs.mapStyle]) {
        return prefs.mapStyle;
      }
    }
  } catch { /* ignore */ }
  return getDefaultMapStyle();
}

/** Returns the preferred tile style object (url + attribution) */
export function getPreferredTileStyle(): { url: string; attribution: string } {
  const style = MAP_STYLES[getPreferredMapStyle()];
  return style || MAP_STYLES['local'];
}
